import * as Sentry from "@sentry/react";
import { tanstackRouterBrowserTracingIntegration } from "@sentry/react";
import { appEnvironment } from "./appEnvironment";
import {
  THIRD_PARTY_SCRIPT_DENY_URLS,
  THIRD_PARTY_SCRIPT_IGNORE_ERRORS,
  isUnhandledNetworkNoise,
} from "./sentryNoise";
import type { AnyRouter } from "@tanstack/react-router";

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Should be called before React renders (in main.tsx)
 *
 * Takes the router because `tanstackRouterBrowserTracingIntegration` needs it
 * at init: it names the pageload transaction after the route id
 * (`/_videoPlayer/$series/$episode`) instead of the raw URL, which would
 * otherwise make one transaction per show and per episode. Adding the
 * integration afterwards is too late — the pageload transaction has already
 * started under the wrong name.
 */
export function initSentry(router: AnyRouter) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = appEnvironment;

  // Only initialize if DSN is provided
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn(
        "[Sentry] VITE_SENTRY_DSN not configured. Sentry error tracking is disabled.",
      );
    }
    return;
  }

  Sentry.init({
    dsn,
    environment,
    // No `release` on purpose: the SDK falls back to the `SENTRY_RELEASE`
    // global that `sentryVitePlugin` injects (vite.config.ts), so events land
    // on the very release the source maps were uploaded under — the version on
    // a tagged build, the git SHA everywhere else.
    integrations: [tanstackRouterBrowserTracingIntegration(router)],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Session Replay (optional - can be enabled later)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Filter out sensitive data and common non-actionable errors
    beforeSend(event, hint) {
      // Don't send errors in development unless explicitly testing
      if (import.meta.env.DEV) {
        console.error("[Sentry] Error captured:", event, hint);
        // Return null to prevent sending in dev, or return event to test
        return null;
      }

      // Filter out common browser extension errors
      if (
        event.exception?.values?.[0]?.value?.includes("chrome-extension://") ||
        event.exception?.values?.[0]?.value?.includes("moz-extension://") ||
        event.exception?.values?.[0]?.value?.includes("safari-extension://")
      ) {
        return null;
      }

      // Fetch's opaque network failures, picked up by the global handlers.
      // Filtered here rather than in `ignoreErrors` so our own catch sites
      // still report — see sentryNoise.ts.
      if (isUnhandledNetworkNoise(event)) {
        return null;
      }

      return event;
    },
    // Scripts injected by the in-app browsers and by Zendesk — see sentryNoise.ts.
    denyUrls: THIRD_PARTY_SCRIPT_DENY_URLS,
    ignoreErrors: [
      ...THIRD_PARTY_SCRIPT_IGNORE_ERRORS,
      // Browser extensions
      "top.GLOBALS",
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "atomicFindClose",
      "fb_xd_fragment",
      "bmi_SafeAddOnload",
      "EBCallBackMessageReceived",
      "conduitPage",
      // Network errors are filtered in `beforeSend`, not here — see
      // sentryNoise.ts.
      // Common third-party script errors
      "Non-Error promise rejection captured",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
  });
}
